import { PlayFab } from './PlayFab';
import { Account } from './Account';

// Notification types that can be received
export type NotificationType = 
  | 'quest_completed'
  | 'battlepass_reward'
  | 'bingo_win'
  | 'trophy_unlocked'
  | 'item_received'
  | 'milestone_completed';

// Notification object structure
export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  icon: string;
  color: string;
  actionUrl?: string; // URL to open when notification is clicked
  data: Record<string, any>;
  read: boolean;
  createdAt: number;
}

// API response types
interface NotificationsResponse {
  notifications: Notification[];
}

interface UnreadCountResponse {
  count: number;
}

// API endpoint for notifications
const NOTIFICATIONS_API_URL = process.env.REACT_APP_NOTIFICATIONS_API_URL || 
  'https://YOUR_API_GATEWAY_URL.execute-api.eu-north-1.amazonaws.com';

export class NotificationService {
  
  /**
   * Get notifications for the current user
   * @param limit Maximum number of notifications to retrieve
   */
  public static async getNotifications(limit: number = 50): Promise<Notification[]> {
    const playFabId = PlayFab.getPlayerId();
    if (!playFabId) {
      console.warn('Cannot get notifications: not logged in');
      return [];
    }

    try {
      // First try CloudScript (more reliable)
      const response = await PlayFab.executeCloudScript('getNotifications', { 
        playFabId, 
        limit 
      });
      
      if (response.success && response.data?.notifications) {
        return response.data.notifications;
      }
      
      // Fallback to direct API call
      const apiResponse = await fetch(
        `${NOTIFICATIONS_API_URL}/notifications?playFabId=${encodeURIComponent(playFabId)}&limit=${limit}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (!apiResponse.ok) {
        throw new Error(`HTTP error: ${apiResponse.status}`);
      }

      const data: NotificationsResponse = await apiResponse.json();
      return data.notifications || [];
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  }

  /**
   * Mark a specific notification as read
   */
  public static async markAsRead(notificationId: string, timestamp: number): Promise<boolean> {
    const playFabId = PlayFab.getPlayerId();
    if (!playFabId) {
      console.warn('Cannot mark notification as read: not logged in');
      return false;
    }

    try {
      const response = await PlayFab.executeCloudScript('markNotificationRead', {
        playFabId,
        notificationId,
        timestamp
      });

      if (response.success) {
        return true;
      }

      // Fallback to direct API
      const apiResponse = await fetch(`${NOTIFICATIONS_API_URL}/notifications/read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playFabId,
          notificationId,
          timestamp
        })
      });

      return apiResponse.ok;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
  }

  /**
   * Mark all notifications as read for the current user
   */
  public static async markAllAsRead(): Promise<boolean> {
    const playFabId = PlayFab.getPlayerId();
    if (!playFabId) {
      console.warn('Cannot mark all as read: not logged in');
      return false;
    }

    try {
      const response = await PlayFab.executeCloudScript('markAllNotificationsRead', {
        playFabId
      });

      if (response.success) {
        return true;
      }

      // Fallback to direct API
      const apiResponse = await fetch(`${NOTIFICATIONS_API_URL}/notifications/read-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ playFabId })
      });

      return apiResponse.ok;
    } catch (error) {
      console.error('Error marking all as read:', error);
      return false;
    }
  }

  /**
   * Get the count of unread notifications
   */
  public static async getUnreadCount(): Promise<number> {
    const playFabId = PlayFab.getPlayerId();
    if (!playFabId) {
      return 0;
    }

    try {
      const response = await PlayFab.executeCloudScript('getUnreadNotificationCount', {
        playFabId
      });

      if (response.success && typeof response.data?.count === 'number') {
        return response.data.count;
      }

      // Fallback to direct API
      const apiResponse = await fetch(
        `${NOTIFICATIONS_API_URL}/notifications/unread-count?playFabId=${encodeURIComponent(playFabId)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (!apiResponse.ok) {
        return 0;
      }

      const data: UnreadCountResponse = await apiResponse.json();
      return data.count || 0;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  /**
   * Get display configuration for a notification type
   */
  public static getNotificationDisplay(type: NotificationType): {
    icon: string;
    color: string;
    defaultTitle: string;
  } {
    const displays: Record<NotificationType, { icon: string; color: string; defaultTitle: string }> = {
      quest_completed: {
        icon: '✅',
        color: '#4CAF50',
        defaultTitle: 'Quest Complete!'
      },
      battlepass_reward: {
        icon: '🎁',
        color: '#9C27B0',
        defaultTitle: 'Battlepass Reward!'
      },
      bingo_win: {
        icon: '🎉',
        color: '#FF9800',
        defaultTitle: 'BINGO!'
      },
      trophy_unlocked: {
        icon: '🏆',
        color: '#FFD700',
        defaultTitle: 'Trophy Unlocked!'
      },
      item_received: {
        icon: '🎨',
        color: '#2196F3',
        defaultTitle: 'New Item!'
      },
      milestone_completed: {
        icon: '🌟',
        color: '#E91E63',
        defaultTitle: 'Milestone Reached!'
      }
    };

    return displays[type] || {
      icon: '🔔',
      color: '#607D8B',
      defaultTitle: 'Notification'
    };
  }

  /**
   * Format timestamp for display
   */
  public static formatTimestamp(timestamp: number): string {
    const now = Date.now();
    const diff = now - timestamp;
    
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) {
      return 'Just now';
    } else if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else if (days < 7) {
      return `${days}d ago`;
    } else {
      return new Date(timestamp).toLocaleDateString();
    }
  }
}

